<ion-header>
  <div
    class="upgrade-feature-banner"
    *ngIf="!featureEnabled && authService.isTenantAdmin"
  >
    <div class="flex-space-between" style="align-items: center">
      Upgrade to run automations
      <ion-button
        fill="outline"
        color="light"
        class="btn-small"
        [routerLink]="'/upgrade'"
        [queryParams]="{ plan: 'startup' }"
      >
        <ion-icon name="arrow-up-circle" class="padded-icon"></ion-icon>
        Upgrade account
      </ion-button>
    </div>
  </div>

  <div class="constrained-content">
    <div class="flex-space-between flexed-align-center" style="padding: 0 13px">
      <div>
        <h3>
          <ion-icon
            name="flash-outline"
            style="font-size: 1.8rem; margin-right: 5px"
          ></ion-icon
          >Automate Actions & Create Custom Workflows
        </h3>
        <p class="subtitle">
          Trigger custom functions in response to conversation updates and
          events.
          <a
            href="https://docs.unthread.io/conversations/automations"
            target="_blank"
          >
            Learn more
            <ion-icon name="chevron-forward-outline"></ion-icon>
          </a>
        </p>
      </div>
      <div>
        <ion-button
          (click)="createAutomation('assistant')"
          [hidden]="
            !automationsEnabled ||
            !canEditAutomations ||
            !isAutomationAssistantEnabled
          "
          color="light"
          ngxTippy="Describe your workflow and let AI generate an automation for you (beta)"
          class="margin-right-5 button-dark-border"
        >
          <ion-icon name="sparkles-outline"></ion-icon>
        </ion-button>
        <ion-button
          (click)="createAutomation()"
          [hidden]="!automationsEnabled || !canEditAutomations"
        >
          <ion-icon name="add-outline"></ion-icon>
          <span class="text-with-left-icon hidden-sm"> Create automation </span>
        </ion-button>
      </div>
    </div>
    <div
      style="padding: 5px 0 13px 0px"
      class="hidden-sm"
      [hidden]="!automationsEnabled || !canEditAutomations"
    >
      <span
        class="tab-nav-link"
        [ngClass]="{ 'tab-nav-active': tab === 'list' }"
        (click)="selectTab('list')"
      >
        Automations
      </span>
      <span
        class="tab-nav-link"
        [ngClass]="{ 'tab-nav-active': tab === 'configuration' }"
        (click)="selectTab('configuration')"
      >
        Configuration
      </span>
      <span
        class="tab-nav-link"
        [ngClass]="{ 'tab-nav-active': tab === 'usage' }"
        (click)="selectTab('usage')"
      >
        Usage
      </span>
    </div>
  </div>
</ion-header>

<ion-content>
  <div class="constrained-content">
    <div *ngIf="tab === 'list'" style="padding: 15px 0">
      <!-- Search and Filter Controls -->
      <div
        class="flexed-align-center"
        *ngIf="automationsEnabled"
        style="margin-bottom: 10px"
      >
        <ion-searchbar
          mode="ios"
          placeholder="Search automations..."
          showCancelButton="never"
          showClearButton="never"
          class="rounded-search-bar"
          [(ngModel)]="searchText"
          (ionChange)="handleFilters()"
          style="height: 32px; margin-left: 10px"
        ></ion-searchbar>
        <ion-select
          [(ngModel)]="selectedTriggerType"
          (ionChange)="handleFilters()"
          placeholder="Trigger Type"
          class="select-outline filter-select"
          interface="popover"
        >
          <ion-select-option value="all">Trigger type</ion-select-option>
          <ion-select-option
            *ngFor="let type of getAvailableTriggerTypes()"
            [value]="type"
          >
            {{ getTriggerTypeLabel(type) }}
          </ion-select-option>
        </ion-select>
        <ion-select
          [(ngModel)]="selectedStatus"
          (ionChange)="handleFilters()"
          placeholder="Status"
          class="select-outline filter-select"
          interface="popover"
        >
          <ion-select-option value="all">Status</ion-select-option>
          <ion-select-option value="active">Status: Active</ion-select-option>
          <ion-select-option value="draft">Status: Paused</ion-select-option>
        </ion-select>
        <ion-button
          *ngIf="
            searchText ||
            selectedTriggerType !== 'all' ||
            selectedStatus !== 'all'
          "
          fill="clear"
          color="medium"
          (click)="clearFilters()"
          class="btn-small"
        >
          <ion-icon name="close-circle-outline" class="padded-icon"></ion-icon>
          Clear filters
        </ion-button>
      </div>

      <ion-card *ngIf="!automationsEnabled">
        <div class="empty-state-container" style="padding: 80px 40px">
          <span class="placeholder-emoji">
            <ion-icon name="flash-outline"></ion-icon>
          </span>
          <h1 class="placeholder-title">Set Up Automations and Triggers</h1>
          <p class="placeholder-subtitle">
            You can integrate with any system and run your own code in response
            to Unthread events.
          </p>
          <div>
            <ion-button
              (click)="requestAccess()"
              *ngIf="!automationsEnabled"
              [disabled]="isLoading"
            >
              Request access
            </ion-button>
          </div>
        </div>
      </ion-card>
      <ion-card class="outline-card" *ngIf="automationsEnabled">
        <ngx-datatable
          class="material datatable-row-borders"
          columnMode="force"
          [limit]="100"
          [headerHeight]="46"
          [footerHeight]="50"
          [rowHeight]="52"
          [scrollbarH]="true"
          [rows]="filteredAutomations"
          [reorderable]="true"
          [loadingIndicator]="isLoading"
        >
          <ngx-datatable-column name="Name" prop="name" [minWidth]="380">
            <ng-template
              let-value="value"
              let-row="row"
              ngx-datatable-cell-template
            >
              <a class="row-title row-link" (click)="editAutomation(row.id)">{{
                value
              }}</a>
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column name="Workflow" prop="trigger" [minWidth]="320">
            <ng-template
              let-value="value"
              let-row="row"
              ngx-datatable-cell-template
            >
              <ion-badge
                color="light"
                *ngIf="value.type === 'webhook'"
                class="row-badge"
              >
                <ion-icon name="custom-webhooks"></ion-icon> webhook
              </ion-badge>
              <ion-badge
                color="light"
                *ngIf="value.type === 'manual'"
                class="row-badge"
              >
                <ion-icon name="build" color="secondary"></ion-icon> manual
              </ion-badge>
              <ion-badge
                color="light"
                *ngIf="value.type === 'event'"
                class="row-badge"
              >
                <ion-icon name="flash" color="tertiary"></ion-icon>
                {{ value.data.objectType }}
                {{ formatTriggerEvent(value.data.event) }}
              </ion-badge>
              <ion-badge
                color="light"
                *ngIf="value.type === 'scheduled'"
                class="row-badge"
              >
                <ion-icon name="time" color="primary"></ion-icon>
                scheduled
              </ion-badge>
              <ion-badge
                color="light"
                *ngIf="value.type === 'slackReaction'"
                class="row-badge"
              >
                <ion-icon name="flash" color="tertiary"></ion-icon>
                reaction added {{ value.data.emojis }}
              </ion-badge>
              <ion-badge
                color="light"
                class="row-badge"
                *ngFor="let step of row.steps"
                [ngxTippy]="getStepLabel(step)"
              >
                <ion-icon [name]="getStepIcon(step)"></ion-icon>
              </ion-badge>
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column
            name="Status"
            prop="status"
            [minWidth]="120"
            [maxWidth]="120"
          >
            <ng-template
              let-value="value"
              let-row="row"
              ngx-datatable-cell-template
            >
              <ion-badge
                color="success"
                *ngIf="value === 'active'"
                class="row-badge"
                >Active
              </ion-badge>
              <ion-badge
                color="warning"
                *ngIf="value !== 'active'"
                class="row-badge"
                >Paused
              </ion-badge>
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column
            name="Description"
            prop="description"
            [minWidth]="300"
          >
            <ng-template
              let-value="value"
              let-row="row"
              ngx-datatable-cell-template
            >
              {{ value }}
            </ng-template>
          </ngx-datatable-column>

          <ngx-datatable-column
            name="Updated"
            prop="updatedAt"
            [minWidth]="120"
            [maxWidth]="120"
          >
            <ng-template
              let-value="value"
              let-row="row"
              ngx-datatable-cell-template
            >
              {{ value | date: "M/d/yyyy" }}
            </ng-template>
          </ngx-datatable-column>
        </ngx-datatable>
      </ion-card>
    </div>
    <ion-grid *ngIf="tab === 'configuration'">
      <ion-row [hidden]="!automationsEnabled">
        <ion-col size-md="3" size="12" class="settings-title-section">
          <h1><ion-icon name="code-outline"></ion-icon>Custom Functions</h1>
          <p class="subtitle">
            Custom functions are written in TypeScript and allow you to write
            your own integrations for use in automations.
          </p>
        </ion-col>
        <ion-col size-md="9" size="12">
          <div class="button-container">
            <ion-button
              (click)="editFunctions()"
              color="dark"
              fill="clear"
              class="button-dark-border"
            >
              <ion-icon name="code-outline"></ion-icon>
              <span class="text-with-left-icon hidden-sm">
                Edit functions
              </span>
            </ion-button>
          </div>
          <ion-card class="outline-card">
            <ngx-datatable
              class="material"
              columnMode="force"
              [limit]="100"
              [headerHeight]="50"
              [footerHeight]="50"
              [rowHeight]="50"
              [scrollbarH]="true"
              [rows]="functions"
              [reorderable]="true"
            >
              <ngx-datatable-column name="Name" prop="name" [minWidth]="200">
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card>
        </ion-col>
      </ion-row>

      <ion-row [hidden]="!automationsEnabled">
        <ion-col size-md="3" size="12" class="settings-title-section">
          <h1>
            <ion-icon name="shield-checkmark-outline"></ion-icon>Automation
            Secrets
          </h1>
          <p class="subtitle">
            Environment variables to be available for use in automations.
          </p>
        </ion-col>
        <ion-col size-md="9" size="12">
          <div class="button-container">
            <ion-button
              (click)="openAddSecretModal()"
              color="dark"
              fill="clear"
              class="button-dark-border"
            >
              <ion-icon name="add-outline"></ion-icon>
              <span class="text-with-left-icon hidden-sm"> Add secret </span>
            </ion-button>
          </div>
          <ion-card class="outline-card">
            <ngx-datatable
              class="material"
              columnMode="force"
              [limit]="100"
              [headerHeight]="50"
              [footerHeight]="50"
              [rowHeight]="50"
              [scrollbarH]="true"
              [rows]="automationSecrets"
              [reorderable]="true"
            >
              <ngx-datatable-column name="Name" prop="name" [minWidth]="300">
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name=""
                prop="name"
                [minWidth]="100"
                [maxWidth]="100"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <a (click)="deleteAutomationSecret(row)">Delete</a>
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card>
        </ion-col>
      </ion-row>

      <ion-row [hidden]="!automationsEnabled">
        <ion-col size-md="3" size="12" class="settings-title-section">
          <h1>
            <ion-icon name="server-outline"></ion-icon>Automation Key-Value
            Stores
          </h1>
          <p class="subtitle">
            Key-Value stores to be available for use in automations.
          </p>
        </ion-col>
        <ion-col size-md="9" size="12">
          <div class="button-container">
            <ion-button
              (click)="openAddStoreModal()"
              color="dark"
              fill="clear"
              class="button-dark-border"
            >
              <ion-icon name="add-outline"></ion-icon>
              <span class="text-with-left-icon hidden-sm"> Add store </span>
            </ion-button>
          </div>
          <ion-card class="outline-card">
            <ngx-datatable
              class="material"
              columnMode="force"
              [limit]="100"
              [headerHeight]="50"
              [footerHeight]="50"
              [rowHeight]="50"
              [scrollbarH]="true"
              [rows]="automationStores"
              [reorderable]="true"
            >
              <ngx-datatable-column name="Name" prop="name" [minWidth]="300">
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name="Description"
                prop="description"
                [minWidth]="300"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  {{ value }}
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name=""
                prop="name"
                [minWidth]="100"
                [maxWidth]="100"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <a (click)="deleteAutomationStore(row.name)">Delete</a>
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card>
        </ion-col>
      </ion-row>
    </ion-grid>

    <!-- Automation Health Tab -->
    <div *ngIf="tab === 'usage'" style="padding: 15px 0">
      <!-- Filters with date picker and refresh button -->
      <div
        class="flex-space-between flexed-align-center"
        style="margin: 0 10px"
      >
        <div class="flexed-align-center">
          <!-- Date Range Controls -->
          <ion-button
            color="dark"
            fill="clear"
            class="button-dark-border btn-small"
            (click)="showDateRangeOptions($event)"
            [ngClass]="{ 'button-round-left': dateRange?.type === 'absolute' }"
            style="margin-right: 8px; margin-left: 6px"
          >
            <ion-icon name="calendar" class="filter-button-icon"></ion-icon>
            {{ dateRange?.label }}
            <ion-icon
              name="chevron-down-outline"
              class="filter-button-caret"
            ></ion-icon>
          </ion-button>

          <!-- Custom date inputs when absolute range selected -->
          <ion-button
            *ngIf="dateRange?.type === 'absolute'"
            color="dark"
            fill="clear"
            class="btn-small"
            (click)="changeDate(false)"
            style="margin-left: 0; margin-right: 0; --padding-end: 6px"
          >
            {{ startDate | date: "M/d/yyyy" }}
          </ion-button>
          <ion-button
            *ngIf="dateRange?.type === 'absolute'"
            color="dark"
            fill="clear"
            class="btn-small"
            disabled="true"
            style="--padding-start: 0; margin: 0; --padding-end: 0"
          >
            –
          </ion-button>
          <ion-button
            *ngIf="dateRange?.type === 'absolute'"
            color="dark"
            fill="clear"
            class="btn-small"
            (click)="changeDate(true)"
            style="--padding-start: 6px; margin-right: 8px"
          >
            {{ endDate | date: "M/d/yyyy" }}
          </ion-button>
          <ion-button
            color="warning"
            class="btn-small"
            *ngIf="isDateRangeInvalid"
            shape="round"
            style="margin-right: 8px"
          >
            <ion-icon name="warning-outline"></ion-icon>
          </ion-button>
          <ion-chip
            mode="ios"
            class="select-chip select-outline"
            [outline]="true"
            (click)="selectAutomationFilter($event)"
          >
            <ion-label>
              <ion-icon
                name="flash-outline"
                class="button-icon"
                style="padding-bottom: 2px"
              ></ion-icon>
              {{ selectedAutomationName }}
              <ion-icon
                name="chevron-down-outline"
                class="icon-padded-left chevron-icon"
              ></ion-icon>
            </ion-label>
          </ion-chip>

          <ion-select
            [(ngModel)]="selectedStatusFilter"
            (ionChange)="filterByStatus()"
            placeholder="Status"
            class="select-outline filter-select"
            interface="popover"
          >
            <ion-select-option value="all">Status</ion-select-option>
            <ion-select-option value="failed">Failed</ion-select-option>
            <ion-select-option value="success">Successful</ion-select-option>
          </ion-select>

          <ion-button
            *ngIf="selectedAutomationFilter || selectedStatusFilter !== 'all'"
            fill="clear"
            color="medium"
            (click)="clearAnalyticsFilters()"
            class="btn-small"
          >
            <ion-icon
              name="close-circle-outline"
              class="padded-icon"
            ></ion-icon>
            Clear filters
          </ion-button>
        </div>

        <div class="flexed-align-center">
          <ion-button fill="clear" class="btn-small" (click)="refreshAllData()">
            <ion-icon name="refresh-outline" class="padded-icon"></ion-icon>
            Refresh
          </ion-button>
        </div>
      </div>

      <!-- Health Check Metrics -->
      <ion-row>
        <ion-col size="12" size-md="3">
          <ion-card class="health-card neutral">
            <div class="health-card-content">
              <div class="health-metric">
                {{ automationMetrics.totalRuns | number }}
              </div>
              <div class="health-label">{{ getCardLabel("runs") }}</div>
              <ion-icon name="calendar-outline" class="health-icon"></ion-icon>
            </div>
          </ion-card>
        </ion-col>
        <ion-col size="12" size-md="3">
          <ion-card
            class="health-card"
            [ngClass]="
              automationMetrics.successRate >= 95
                ? 'success'
                : automationMetrics.successRate >= 85
                  ? 'warning'
                  : 'failure'
            "
          >
            <div class="health-card-content">
              <div class="health-metric">
                {{ automationMetrics.successRate | number: "1.1-1" }}%
              </div>
              <div class="health-label">{{ getCardLabel("success") }}</div>
              <ion-icon
                name="checkmark-circle-outline"
                class="health-icon"
              ></ion-icon>
            </div>
          </ion-card>
        </ion-col>
        <ion-col size="12" size-md="3">
          <ion-card
            class="health-card"
            [ngClass]="automationMetrics.failedRuns > 0 ? 'failure' : 'success'"
          >
            <div class="health-card-content">
              <div class="health-metric">
                {{ automationMetrics.failedRuns | number }}
              </div>
              <div class="health-label">{{ getCardLabel("failed") }}</div>
              <ion-icon
                [name]="
                  automationMetrics.failedRuns > 0
                    ? 'alert-circle-outline'
                    : 'checkmark-circle-outline'
                "
                class="health-icon"
              ></ion-icon>
            </div>
          </ion-card>
        </ion-col>
        <ion-col size="12" size-md="3">
          <ion-card class="health-card neutral">
            <div class="health-card-content">
              <div class="health-metric-small">
                {{
                  automationMetrics.lastActivity
                    ? (automationMetrics.lastActivity.timestamp ||
                        automationMetrics.lastActivity.startedAt
                      | date: "short")
                    : "No activity"
                }}
              </div>
              <div class="health-label">{{ getCardLabel("activity") }}</div>
              <ion-icon name="time-outline" class="health-icon"></ion-icon>
            </div>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Loading State -->
      <div
        *ngIf="isLoadingMetrics"
        class="loading-container"
        style="text-align: center; padding: 40px"
      >
        <ion-spinner name="crescent"></ion-spinner>
        <p style="margin-top: 10px; color: var(--ion-color-medium)">
          Loading automation data...
        </p>
      </div>

      <!-- Paginated Automation Runs Table -->
      <div
        *ngIf="
          !isLoadingMetrics &&
          automationsEnabled &&
          automationMetrics.totalRuns > 0
        "
      >
        <ion-card class="outline-card">
          <ion-card-header>
            <ion-card-title>Automation Activity</ion-card-title>
            <ion-card-subtitle
              >Monitor automation performance and quickly address any
              issues</ion-card-subtitle
            >
          </ion-card-header>
          <ion-card-content style="padding: 0">
            <ngx-datatable
              class="material datatable-row-borders"
              columnMode="force"
              [headerHeight]="46"
              [footerHeight]="50"
              [rowHeight]="60"
              [scrollbarH]="true"
              [reorderable]="false"
              [sortType]="'single'"
              paginated
              [dataSource]="automationRunsDataSource"
              [loadingIndicator]="isLoadingAutomationRuns"
            >
              <ngx-datatable-column
                name="Time"
                prop="startedAt"
                [sortable]="true"
                [width]="140"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <span class="table-cell-content">{{
                    value || row.timestamp || row.failedAt
                      | date: "MMM d, yyyy, h:mm:ss a"
                  }}</span>
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name="Status"
                prop="status"
                [sortable]="true"
                [width]="75"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <ion-badge
                    color="success"
                    *ngIf="value === 'completed'"
                    class="row-badge"
                    >Success</ion-badge
                  >
                  <ion-badge
                    color="danger"
                    *ngIf="value === 'failed'"
                    class="row-badge"
                    >Failed</ion-badge
                  >
                  <ion-badge
                    color="warning"
                    *ngIf="value === 'running'"
                    class="row-badge"
                    >Running</ion-badge
                  >
                  <ion-badge
                    color="medium"
                    *ngIf="value === 'pending'"
                    class="row-badge"
                    >Pending</ion-badge
                  >
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name="Trigger Type"
                prop="automation.trigger.type"
                [sortable]="true"
                [width]="95"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <ion-badge
                    color="light"
                    *ngIf="getTriggerType(row) === 'webhook'"
                    class="row-badge"
                  >
                    <ion-icon name="custom-webhooks"></ion-icon> webhook
                  </ion-badge>
                  <ion-badge
                    color="light"
                    *ngIf="getTriggerType(row) === 'manual'"
                    class="row-badge"
                  >
                    <ion-icon name="build" color="secondary"></ion-icon> manual
                  </ion-badge>
                  <ion-badge
                    color="light"
                    *ngIf="getTriggerType(row) === 'event'"
                    class="row-badge"
                  >
                    <ion-icon name="flash" color="tertiary"></ion-icon> event
                  </ion-badge>
                  <ion-badge
                    color="light"
                    *ngIf="getTriggerType(row) === 'scheduled'"
                    class="row-badge"
                  >
                    <ion-icon name="time" color="primary"></ion-icon> scheduled
                  </ion-badge>
                  <ion-badge
                    color="light"
                    *ngIf="getTriggerType(row) === 'slackReaction'"
                    class="row-badge"
                  >
                    <ion-icon name="flash" color="tertiary"></ion-icon> reaction
                  </ion-badge>
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name="Automation"
                prop="automation.name"
                [sortable]="true"
                [minWidth]="200"
                [flexGrow]="2"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <a
                    class="row-title row-link text-content-auto"
                    (click)="viewAutomation(row)"
                    [title]="row.automation.name"
                    >{{ row.automation.name }}</a
                  >
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name="Conversation"
                prop="conversation.friendlyId"
                [sortable]="true"
                [minWidth]="250"
                [flexGrow]="3"
              >
                <ng-template
                  let-value="value"
                  let-row="row"
                  ngx-datatable-cell-template
                >
                  <a
                    *ngIf="value && row.conversation?.friendlyId"
                    class="row-title row-link text-content-auto"
                    (click)="viewConversation(row)"
                    [title]="getFormattedConversationTitle(row)"
                  >
                    <span
                      [innerHTML]="getFormattedConversationTitle(row)"
                    ></span>
                  </a>
                  <span
                    *ngIf="!value || !row.conversation?.friendlyId"
                    class="table-cell-content text-muted"
                    >No conversation</span
                  >
                </ng-template>
              </ngx-datatable-column>

              <ngx-datatable-column
                name="Actions"
                [canAutoResize]="false"
                [sortable]="false"
                [width]="90"
              >
                <ng-template let-row="row" ngx-datatable-cell-template>
                  <div class="run-actions">
                    <ion-button
                      fill="clear"
                      size="small"
                      (click)="viewRunLogs(row)"
                      title="View detailed logs"
                      ngxTippy
                      [tippyProps]="{
                        touch: false,
                        arrow: false,
                        placement: 'bottom',
                        content: 'View detailed logs'
                      }"
                    >
                      <ion-icon name="document-text-outline"></ion-icon>
                    </ion-button>
                  </div>
                </ng-template>
              </ngx-datatable-column>
            </ngx-datatable>
          </ion-card-content>
        </ion-card>
      </div>
      <!-- Empty State -->
      <div
        *ngIf="automationMetrics.totalRuns === 0 && !isLoadingMetrics"
        class="empty-state-container"
        style="padding: 60px 40px"
      >
        <ion-icon
          name="flash-outline"
          style="font-size: 4rem; color: var(--ion-color-medium)"
        ></ion-icon>
        <h3 style="margin-top: 20px; color: var(--ion-color-medium)">
          No Automation Activity
        </h3>
        <p style="color: var(--ion-color-medium); margin-bottom: 20px">
          No automation runs detected in the selected time period.
        </p>
        <ion-button fill="outline" size="small" (click)="selectTab('list')">
          <ion-icon name="list-outline" slot="start"></ion-icon>
          View All Automations
        </ion-button>
      </div>
    </div>
  </div>
</ion-content>

<ion-modal
  [isOpen]="showAutomationAddSecretModal"
  (willDismiss)="onDismissAutomationAddSecretModal()"
  class="automation-add-secret-modal"
>
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Add Automation Secret</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onDismissAutomationAddSecretModal()"
            ><ion-icon name="close"></ion-icon
          ></ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div style="padding: 20px">
        <ion-label>
          <strong>Name</strong>
        </ion-label>
        <ion-input
          placeholder="e.g. API_TOKEN"
          [(ngModel)]="automationSecret.name"
          class="input-field"
        >
        </ion-input>
        <ion-label>
          <strong>Value</strong>
        </ion-label>
        <ion-textarea
          [(ngModel)]="automationSecret.value"
          class="input-field code"
          rows="14"
        >
        </ion-textarea>
      </div>
    </ion-content>
    <ion-footer class="footer flex-space-between">
      <ion-button (click)="addAutomationSecret()" [disabled]="isLoading">
        Save
        <ion-spinner
          name="crescent"
          [hidden]="!isLoading"
          style="margin-left: 10px"
        ></ion-spinner>
      </ion-button>
    </ion-footer>
  </ng-template>
</ion-modal>

<ion-modal
  [isOpen]="showAutomationAddStoreModal"
  (willDismiss)="onDismissAutomationAddStoreModal()"
  class="automation-add-secret-modal"
>
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Add Automation Key-Value Store</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onDismissAutomationAddStoreModal()"
            ><ion-icon name="close"></ion-icon
          ></ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <div style="padding: 20px">
        <ion-label>
          <strong>Name</strong>
        </ion-label>
        <ion-input
          placeholder="e.g. my_key_value_store"
          [(ngModel)]="automationStore.name"
          class="input-field"
        >
        </ion-input>
        <ion-label>
          <strong>Description</strong>
        </ion-label>
        <ion-textarea
          [(ngModel)]="automationStore.description"
          class="input-field"
          rows="5"
        >
        </ion-textarea>
      </div>
    </ion-content>
    <ion-footer class="footer flex-space-between">
      <ion-button (click)="addAutomationStore()" [disabled]="isLoading">
        Save
        <ion-spinner
          name="crescent"
          [hidden]="!isLoading"
          style="margin-left: 10px"
        ></ion-spinner>
      </ion-button>
    </ion-footer>
  </ng-template>
</ion-modal>
